// The shell: opens a trace (drop, picker, ?run=, ?trace=), builds the shared context once,
// and switches between the UIs in js/ui/registry.js. Nothing here knows how a UI draws.

import { $, el, fmtPs, fmtInt, bytes, esc } from "./dom.js";
import { createStore, readUrl, readHash, writeHash } from "./state.js";
import { loadFrom } from "./load.js";
import { Model } from "./model.js";
import { buildLinks } from "./links.js";
import { placeHost } from "./anchor.js";
import { BinsCache } from "./bins.js";
import { Disasm } from "./disasm.js";
import { UIS } from "./ui/registry.js";
import { openProgram } from "./program.js";
import { highlightPython } from "./hl.js";
import { mark, marks, phases } from "./perf.js";
import { armGuide, offerGuide, toggleGuide } from "./guide.js";

mark("modules");

const store = createStore({
  ui: null, view: null, brush: null, cursor: NaN, hover: null, selection: null,
  expanded: null, subrows: { units: false, dma: true }, filter: "",
  chip: null, tc: null, page: "map",
});

let ctx = null;
let current = null;
const root = $("#ui-root");
const url = readUrl();

// --- opening ----------------------------------------------------------------------

function progress(text) {
  $("#progress").textContent = text;
  $("#loadinfo").textContent = text;
}

// A thin bar under the header: the download by bytes over the first 70%, then the
// phases that follow it by the share of a load they usually are.
const PHASE_AT = { fetch: 0, read: 0.7, decode: 0.78, model: 0.84, links: 0.9, anchors: 0.94, bins: 0.95, mount: 0.97, paint: 1 };
function loadBar(fraction) {
  const b = $("#loadbar");
  if (fraction == null) { b.hidden = true; b.style.width = "0"; return; }
  b.hidden = false;
  b.style.width = (Math.max(0, Math.min(1, fraction)) * 100).toFixed(1) + "%";
}

async function open(source, opts = {}) {
  progress("reading...");
  let result;
  try {
    loadBar(0.02);
    result = await loadFrom(source, { gen: url.gen, shape: url.shape, ...opts }, (p) => {
      // Events are what a reader counts in; the bytes stand in for them in proportion
      // until the file is in, and speak for themselves when the count is unknown.
      const frac = p.size ? Math.min(1, p.bytes / p.size) : 0;
      const read = p.events && p.size ? `${fmtInt(Math.round(frac * p.events))} of ${fmtInt(p.events)} events`
        : `${bytes(p.bytes)}${p.size ? " of " + bytes(p.size) : ""}`;
      progress(`reading ${read}${p.lines ? ", " + fmtInt(p.lines) + " lines" : ""}${p.note ? " · " + p.note : ""}`);
      if (p.note) loadBar(PHASE_AT.read);
      else if (p.size) loadBar(0.02 + 0.68 * Math.min(1, p.bytes / p.size));
    });
    loadBar(PHASE_AT.decode);
  } catch (e) {
    loadBar(null);
    progress("");
    console.error(e);
    root.innerHTML = `<section id="welcome" class="load-error"><h1>Could not open this run</h1>
      <p>The run finished on the server; the viewer hit an error loading its trace.</p>
      <pre class="err">${esc(e.message || String(e))}</pre>
      <p class="dim">Reload to try again, or <a href="./">go back to the runs</a>. If it keeps
      happening after a hard reload (Shift-click reload), the run may need re-opening.</p></section>`;
    return;
  }
  const data = result.data;
  if (!data.meta.timing || !(data.meta.t_end_ps > 0)) {
    progress(""); loadBar(null);
    root.innerHTML = `<section id="welcome"><h1>No time in this trace</h1><p>It was recorded without
      <code>SYMTPU_TIMING</code> (no <code>t</code>/<code>at</code> fields), so nothing in it has a time
      to draw against. Re-run with <code>SYMTPU_TIMING=1</code>; the playground always does.</p>
      <p class="dim">${esc(result.name)}: ${fmtInt(data.meta.events)} events, ${fmtInt(data.cores.length)} cores.</p></section>`;
    return;
  }
  const t0 = performance.now();
  const model = new Model(data, { gen: opts.gen || url.gen, shape: opts.shape || url.shape });
  mark("model"); loadBar(PHASE_AT.model);
  const links = buildLinks(model);
  mark("links"); loadBar(PHASE_AT.links);
  const anchors = placeHost(model, links);
  mark("anchors");
  if (ctx && ctx.load && ctx.load.close) ctx.load.close();
  ctx = { model, links, anchors, bins: new BinsCache(model), store, name: result.name, source: result.source, load: result, disasm: new Disasm(model, result.render || null, result.listing || null) };
  mark("bins"); loadBar(PHASE_AT.bins);
  window.__ctx = ctx;   // for the headless tests
  const ms = performance.now() - t0;
  ctx.timing = { read: result.ms, model: ms };
  progress("");
  describeRun(ctx);
  mark("topology");
  const h = readHash();
  showUI(h.ui || url.ui || store.get().ui || "timeline", h);
  mark("mount"); loadBar(PHASE_AT.mount);
  requestAnimationFrame(() => {
    mark("paint"); loadBar(null);
    // The first run a visitor opens explains itself, once the run is on the screen.
    offerGuide();
    console.debug("open: " + phases(marks).map(([n, d]) => `${n} ${d.toFixed(0)}`).join(" \u00b7 ") + ` = ${performance.now().toFixed(0)} ms`);
  });
}

// --- the run, and the way to another ---------------------------------------------

// The precomputed examples the server lists, fetched once: the welcome page, the
// open menu and the run sheet all read from it.
let examples = null;
async function loadExamples() {
  if (examples) return examples;
  try {
    const r = await fetch(new URL("runs/precomputed.json", location.href));
    examples = r.ok ? (await r.json()).examples.filter((e) => e.status === "ok") : [];
  } catch (_) { examples = []; }
  return examples;
}

// The program panel takes the main area over, whatever was in it; the run it opens
// arrives as a normal ?run= load, so there is nothing to come back to.
async function openCustom(program = null, topology = null) {
  if (current) { current.app.unmount(); current = null; }
  for (const p of document.querySelectorAll(".pop")) p.hidden = true;
  try {
    if (program != null) localStorage.setItem("playground.program", program);
    if (topology != null) localStorage.setItem("playground.topology", topology);
  } catch (_) { /* private window */ }
  openProgram(root, await loadExamples());
}

function fillRuns(box) {
  box.textContent = "";
  // First, a program of your own; then the precomputed runs.
  const custom = el("a", "custom");
  custom.href = "#";
  custom.innerHTML = `<div class="t">Custom</div><div class="k">write a JAX / Flax program, pick a slice, simulate it</div>`;
  custom.onclick = (e) => { e.preventDefault(); openCustom(); };
  box.appendChild(custom);
  for (const ex of examples || []) {
    const a = el("a");
    // Every run is named by its key alone; where it is -- beside the page, or at the
    // API, which is where an export leaves a run too big for the host -- is for
    // js/load.js to find, so the same link works on either tree.
    a.href = `?run=${ex.key}`;
    a.innerHTML = `<div class="t">${esc(ex.title)}</div><div class="k">${esc(ex.topology)} · ${esc(ex.description || "")}</div>`;
    box.appendChild(a);
  }
}

// The header names the run; the sheet under it says what the run was: the program,
// the generation and slice it ran on, what the trace holds and where it came from.
async function describeRun(c) {
  const { model, load } = c;
  const meta = load.meta || {};
  const key = (load.source || "").match(/\/runs\/([0-9a-f]{24})\//);
  // The run's own directory, not the page's: a run the API made lives where the API is
  // when that is another origin (js/api.js), and its files are named from there.
  const dir = key ? new URL("./", load.source).href : null;
  const ex = key ? (await loadExamples()).find((e) => e.key === key[1]) : null;
  if (c !== ctx) return;
  const name = ex ? ex.title : c.name;
  const nm = $("#run-name");
  nm.textContent = name;
  nm.title = name;
  nm.hidden = false;
  $("#run-btn").hidden = false;
  $("#guide-btn").hidden = false;
  armGuide();
  document.title = `${name} · symtpu trace viewer`;
  const shape = model.shape.join("×"), chips = model.shape.reduce((a, x) => a * x, 1);
  const rows = [
    ["program", ex ? `${ex.name} (${ex.file || "examples/" + ex.name.replace(/_v\d\w*$/, "") + ".py"})` : c.name],
    ["about", ex ? ex.description : null],
    ["generation", model.gen + (model.inferred.gen ? " (inferred from the trace)" : "")],
    ["slice", `${shape}, ${chips} chip${chips === 1 ? "" : "s"}` + (model.inferred.shape ? " (inferred)" : "")],
    ["cores", `${model.cores.filter((x) => x.kind === "tc").length} TensorCore streams, ${model.cores.filter((x) => x.kind === "sc").length} SparseCore, ${model.cores.filter((x) => x.kind === "tile").length} tile`],
    ["trace", `${fmtInt(model.meta.events)} events, ${fmtPs(model.tEnd)} of virtual time${model.truncated ? ", truncated at the cap" : ""}`],
    ["run", meta.status ? `${meta.status}, ${(meta.wall_ns / 1e9).toFixed(1)} s of wall time, ${bytes(meta.memory_peak || 0)} peak` : null],
    ["key", key ? key[1] : null],
    ["read", `${load.kind}${load.gz ? ".gz" : ""} in ${c.timing.read.toFixed(0)} ms, modelled in ${c.timing.model.toFixed(0)} ms`],
  ];
  const pop = $("#run-pop");
  pop.textContent = "";
  pop.appendChild(el("div", "strong", name));
  const kv = el("div", "kv");
  for (const [k, v] of rows) { if (v == null) continue; kv.appendChild(el("span", "k", k)); kv.appendChild(el("span", null, v)); }
  pop.appendChild(kv);
  if (key) {
    const links = el("div", "links");
    for (const f of ["program.py", "stdout.txt", "meta.json"]) { const a = el("a", null, f); a.href = dir + f; a.target = "_blank"; links.appendChild(a); }
    pop.appendChild(links);
    // The program itself, and the way to run a variation of it.
    fetch(dir + "program.py").then((r) => (r.ok ? r.text() : null)).then((text) => {
      if (text == null || c !== ctx) return;
      const head = el("div", "src-head");
      head.appendChild(el("span", "strong", "program.py"));
      const edit = el("a", "act", "open as Custom");
      edit.href = "#";
      edit.onclick = (e) => { e.preventDefault(); openCustom(text, ex ? ex.topology : null); };
      head.appendChild(edit);
      pop.appendChild(head);
      const pre = el("pre", "src");
      pre.appendChild(highlightPython(text));
      pop.appendChild(pre);
    });
  }
}

// One popover open at a time; a click elsewhere or Escape closes it.
function popover(btn, pop, onOpen) {
  btn.onclick = (e) => {
    e.stopPropagation();
    const show = pop.hidden;
    for (const p of document.querySelectorAll(".pop")) p.hidden = true;
    if (show && onOpen) onOpen();
    pop.hidden = !show;
  };
  pop.onclick = (e) => e.stopPropagation();
}
document.addEventListener("click", () => { for (const p of document.querySelectorAll(".pop")) p.hidden = true; });
document.addEventListener("keydown", (e) => { if (e.key === "Escape") for (const p of document.querySelectorAll(".pop")) p.hidden = true; });
$("#guide-btn").onclick = () => toggleGuide();
popover($("#run-btn"), $("#run-pop"));
popover($("#open-btn"), $("#open-pop"), () => loadExamples().then(() => fillRuns($("#open-runs"))));

function showUI(id, hash = {}, force = false) {
  const spec = UIS.find((u) => u.id === id) || UIS[0];
  if (current && !force && current.id === spec.id) return;
  if (current) { current.app.unmount(); current = null; }
  root.textContent = "";
  root.classList.remove("welcome");
  const app = spec.create(ctx);
  app.id = spec.id;
  current = { id: spec.id, app };
  store.set({ ui: spec.id });
  app.mount(root);
  if (app.applyHash) app.applyHash(hash);
  syncHash();
}

function syncHash() {
  if (!current) return;
  writeHash({ ui: current.id, ...(current.app.toHash ? current.app.toHash() : {}) });
}
store.on("*", () => syncHash());

// --- inputs ------------------------------------------------------------------------


document.addEventListener("dragover", (e) => { e.preventDefault(); root.classList.add("dragging"); });
document.addEventListener("dragleave", () => root.classList.remove("dragging"));
document.addEventListener("drop", (e) => {
  e.preventDefault();
  root.classList.remove("dragging");
  const f = e.dataTransfer.files[0];
  if (f) open(f);
});

function main() {
  if (url.run) open({ run: url.run });
  else loadExamples().then(() => fillRuns($("#runs")));
}
main();
